/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { selectUser } from "../../../redux/user/userSlice";
import { useSelector } from "react-redux";
import ClassroomsList from "./ClassroomsList";
import { useAppDispatch } from "../../../app/hooks";
import { retrieveSchoolClassrooms, schoolClassroomsSlice, selectSchoolClassrooms } from "../../../redux/school/schoolClassroomsSlice";
import { retrieveSchool, SchoolInfo, schoolSlice, selectSchool } from "../../../redux/school/schoolSlice";
import { LoadState } from "../../../redux/common";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { ContextMenu, ContextMenuOptions } from "../../../features/base/ContextMenu";
import { classroomInsUpdSlice } from "../../../redux/classroom/classroomInsUpdSlice";
import SchoolPageTitle from "../basics/SchoolPageTitle";
import SchoolPageAvatar from "../basics/SchoolPageAvatar";
import SchoolUsersList from "./SchoolUsersList";
import PageAuthenticatedLoading from "../../../features/pages/PageAuthenticatedLoading";
import { schoolDelete, schoolInsUpdSlice } from "../../../redux/school/schoolInsUpdSlice";
import { isTokenReady } from "../../../features/user/UserAvatar";
import { imageInsSlice } from "../../../redux/image/imageInsSlice";
import MenuIcon from "@mui/icons-material/Menu";
import { hasPermission, checkIsAdmin, PERMISSIONS_TO_ALLOW_CONVERSATION, PERMISSIONS, ObjectTarget, PermissionId, hasDirectPermission } from "../../../app/permissions";
import { usersForPermissionsSlice } from "../../../redux/user/usersForPermissionsSlice";
import { routeSchoolEdit, routeSchoolImage } from "../../../routes/school";
import { routeClassroomNew } from "../../../routes/classroom";
import { ElementMissingContext } from "../../../features/base/ElementMissingContext";
import SchoolImages from "./SchoolImages";
import { AppBar, Box, Button, ButtonGroup, Toolbar, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { TopBar } from "../../../features/top/TopBar";
import { useLocation } from "react-router-dom";

import { SchoolMessages } from "../messages/SchoolConversation";
import DptoList from "./DptoList";
import RoutineList from "./RoutineList ";
import MassiveMessageReport from "../massive/MassiveMessageReport";
import SurveyReport from "../../../features/survey/SurveyReport";

import ShieldIcon from '@mui/icons-material/Shield';
import SchoolIcon from "@mui/icons-material/School";
import RoomPreferencesIcon from '@mui/icons-material/RoomPreferences';
import CollectionsIcon from "@mui/icons-material/Collections";
import MessageIcon from "@mui/icons-material/Message";
import EditIcon from "@mui/icons-material/Edit";
import CasesIcon from '@mui/icons-material/Cases';
import TooltipButton from "../../../components/TooltipButton";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SchoolStudentList from "./SchoolStudentList";

const VALID_TABS = [
    "classroom", "permissions", "routine", "department", 
    "images", "messages", "massive-report", "survey-report", "students"
];

export type SchoolContentOptions = typeof VALID_TABS[number];

export function School() {
    const user = useSelector(selectUser);
    const classrooms = useSelector(selectSchoolClassrooms);
    const school = useSelector(selectSchool);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        dispatch(classroomInsUpdSlice.actions.clean());
        dispatch(schoolInsUpdSlice.actions.clean());
    }, []);

    // Function to get initial tab from URL hash
    const getInitialTabFromHash = (): SchoolContentOptions => {
        const hash = location.hash.replace('#', '');
        if (VALID_TABS.includes(hash as SchoolContentOptions)) {
            return hash as SchoolContentOptions;
        }
        return "classroom";
    };

    const [ contentSelected, setContentSelected ] = useState<SchoolContentOptions>(getInitialTabFromHash());

    const { schoolId } = useParams();

    useEffect(() => {
        if (school.selected && school.selected.id != schoolId) {
            dispatch(schoolSlice.actions.clean());
        }
        if (school.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveSchool({ schoolId: schoolId }));
        }
    }, [schoolId, school, user]);

    useEffect(() => {
        if (classrooms.schoolId && classrooms.schoolId != schoolId) {
            dispatch(schoolClassroomsSlice.actions.clean());
        }
        if (classrooms.state.state == LoadState.NotStarted && isTokenReady(user) && school.selected?.id)
            dispatch(retrieveSchoolClassrooms({ schoolId: school.selected.id }));    }, [classrooms, school.selected, schoolId, user]);

    // Listen for hash changes to update content selection
    useEffect(() => {
        const handleHashChange = () => {
            const newTab = getInitialTabFromHash();
            setContentSelected(newTab);
        };

        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);
        
        // Also update on location change (for initial load or navigation)
        const newTab = getInitialTabFromHash();
        if (newTab !== contentSelected) {
            setContentSelected(newTab);
        }

        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [location.hash, contentSelected]);

    if (!schoolId) {
        return <ElementMissingContext />;
    }

    const isAdmin = checkIsAdmin(user.priza.info);
    const isSchoolAdmin: boolean = hasPermission(user.priza.info, PermissionId.SCHOOL_ADMIN, schoolId);
    
    const canSendMessage = () => {
        for(const permission of PERMISSIONS_TO_ALLOW_CONVERSATION) {
            const perm = PERMISSIONS[permission];
            if (!perm) {
                continue;
            }
            if (perm.target != ObjectTarget.SCHOOL) {
                continue;
            }
            if (hasDirectPermission(user.priza.info, permission, schoolId)) {
                return true;
            }
        }
        return false;
    };
    
    // Function to handle tab change and update URL hash
    const handleTabChange = (tab: string) => {
        if (VALID_TABS.includes(tab as SchoolContentOptions)) {
            setContentSelected(tab);
            window.location.hash = tab;
        } else {
            setContentSelected("classroom");
            window.location.hash = "classroom";
        }
    };

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string|null>(null);

    const handleDeleteClick = () => {
        setOpenDeleteDialog(true);
    };

    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);
        setDeleteError(null);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        setDeleteError(null);
        try {
            // TODO: call the actual delete API here
            await dispatch(schoolDelete({ schoolId: schoolId! }));
            setOpenDeleteDialog(false);
            navigate("/");
        } catch (err: any) {
            setDeleteError("Erro ao remover escola. Tente novamente.");
            setDeleting(false);
        }
    };

    const options: ContextMenuOptions[] = [];
    options.push({
        label: "Atualizar", click: () => {
            dispatch(schoolSlice.actions.clean());
            dispatch(schoolClassroomsSlice.actions.clean());
        }
    });
    if (isSchoolAdmin) {
        options.push({
            label: "Editar", click: () => {
                if (school.selected) {
                    const newSchool = { ...school.selected };
                    dispatch(schoolInsUpdSlice.actions.set(newSchool));
                    navigate(routeSchoolEdit(schoolId));
                }
            }
        });
        options.push({
            label: "Alterar Imagem", click: () => {
                if (school.selected) {
                    dispatch(imageInsSlice.actions.clean());
                    navigate(routeSchoolImage(schoolId));
                }
            }
        });
        options.push({
            label: "Nova sala de aula", click: () => {
                dispatch(classroomInsUpdSlice.actions.clean());
                navigate(routeClassroomNew(schoolId));
            }
        });
    }
    if (isAdmin) {
        options.push({label: "Remover", click: handleDeleteClick });
    }

    // console.log(`SchoolId: ${schoolId}/${school.state.state}, Classrooms: ${classrooms.state.state}`);
    return <>
        <Dialog open={openDeleteDialog} onClose={handleDeleteCancel} aria-labelledby="delete-school-dialog-title">
            <DialogTitle id="delete-school-dialog-title" >Remover Escola</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Esta ação é <b>irreversível</b> e todos os dados associados serão perdidos!
                </DialogContentText>
                <DialogContentText sx={{ color: 'error.main' }}>
                    Tem certeza que deseja <b>REMOVER</b> a escola <b>{school.selected?.name}</b>?
                </DialogContentText>
                {deleteError && <DialogContentText color="error">{deleteError}</DialogContentText>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDeleteCancel} 
                    color="primary" variant="contained" disabled={deleting}>Cancelar</Button>
                <Button onClick={handleDeleteConfirm} 
                    color="error" variant="contained" disabled={deleting} autoFocus>
                    {deleting ? "Removendo..." : "Remover"}
                </Button>
            </DialogActions>
        </Dialog>
        <PageAuthenticatedLoading
        topbar={
            <TopBar
                topMenu={<ContextMenu label={<MenuIcon />} options={options} />}
                breadcrumbs={[
                    { label: school.selected?.name || "Escola" }
                ]}
                title={<Typography variant="h6" component="div">Escola</Typography>}
            />
        }
        loading={{
            states: [school.state],
            content: () => {
                const body = () => {
                    switch (contentSelected) {
                        case "classroom": return <ClassroomsList schoolId={schoolId} />;
                        case "routine": return <RoutineList schoolId={schoolId} />;
                        case "department": return <DptoList schoolId={schoolId} />;
                        case "permissions": return <SchoolUsersList schoolId={schoolId} />;
                        case "images": return <SchoolImages schoolId={schoolId} canDeleteImage={isSchoolAdmin} />;
                        case "messages": return <SchoolMessages school={school.selected!} />;
                        case "massive-report": return <MassiveMessageReport schoolId={schoolId} />;
                        case "survey-report": return <SurveyReport target={ObjectTarget.SCHOOL} targetId={schoolId} />;
                        case "students": return <SchoolStudentList schoolId={schoolId} />;
                        // case "addClassroom": <Typography variant="h6" component="div">Adicionar Sala de Aula</Typography>;
                    }
                };
                return <>
                    <AppBar key={"app-name"} id={"app-name"} position="static" color="default" elevation={5}>
                        <Toolbar>
                            <SchoolPageAvatar />
                            <SchoolPageTitle schoolId={schoolId} />
                            <Box sx={{ flexGrow: 1 }} />
                            <Button onClick={() => navigate("/")}>Voltar</Button>
                        </Toolbar>
                    </AppBar>
                    <AppBar key={"app-control"} id={"app-control"} position="static" color="default" elevation={2}>
                        <Toolbar>
                            <ButtonGroup variant="contained" color="primary">
                                <TooltipButton
                                    option="classroom"
                                    optionSelected={contentSelected}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Salas de Aula">
                                    <SchoolIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="permissions"
                                    optionSelected={contentSelected}
                                    disabled={!isSchoolAdmin}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Responsáveis">
                                        <ShieldIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="routine"
                                    optionSelected={contentSelected}
                                    disabled={!isSchoolAdmin}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Rotinas">
                                    <RoomPreferencesIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="department"
                                    disabled={!isSchoolAdmin}
                                    optionSelected={contentSelected}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Departamentos">
                                    <CasesIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="images"
                                    optionSelected={contentSelected}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Imagens">
                                    <CollectionsIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="messages"
                                    optionSelected={contentSelected}
                                    onClick={(option) => handleTabChange(option)}
                                    disabled={!canSendMessage()}
                                    tooltip="Mensagens">
                                    <MessageIcon />
                                </TooltipButton>
                                {/* <TooltipButton
                                    option="massive-report"
                                    optionSelected={contentSelected}
                                    disabled={!isSchoolAdmin}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Relatório de Mensagens Massivas">
                                    <ForumIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="survey-report"
                                    optionSelected={contentSelected}
                                    disabled={!isSchoolAdmin}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Resulado de enquetes">
                                    <PollIcon />
                                </TooltipButton> */}
                                <TooltipButton
                                    option="students"
                                    optionSelected={contentSelected}
                                    disabled={!isSchoolAdmin}
                                    onClick={(option) => handleTabChange(option)}
                                    tooltip="Procurar alunos">
                                    <PeopleAltIcon />
                                </TooltipButton>
                                <TooltipButton
                                    option="edit-school"
                                    optionSelected={contentSelected}
                                    disabled={!isSchoolAdmin}
                                    onClick={() => {
                                            const newSchool = { ...school.selected } as SchoolInfo;
                                            dispatch(schoolInsUpdSlice.actions.set(newSchool));
                                            navigate(routeSchoolEdit(schoolId));
                                        }
                                    }
                                    tooltip="Editar Sala">
                                    <EditIcon />
                                </TooltipButton>
                            </ButtonGroup>
                        </Toolbar>
                    </AppBar>
                    {body()}
                </>;
            },
            error: "Erro ao carregar escola",
            retry: () => {
                dispatch(schoolSlice.actions.clean());
                dispatch(schoolClassroomsSlice.actions.clean());
                dispatch(usersForPermissionsSlice.actions.clean());
            }
        }} />
    </>;

}
